import i18n from 'i18next';

import { FieldsMap, PassengerField } from '../Passenger/types';
import { validate } from '../Passenger/utils';
import { TravellerFields } from '../TravellerForm/types';
import {
	DocTypes,
	OptionParameterFragment,
	PassengerFragment,
	TravellerFieldEnum,
	TravellerProfileFragment
} from '@websky/graphql';
import { validateDisabilities } from './components/components/Disabilities/utils';
import { PassengerType } from '../enums';
import { format, parseISO } from 'date-fns';
import { convertGenderToCorrectValue } from '../utils';
import memes from '../memes';

export const requiredFillData = (
	passengerFormValues: any,
	customerFormValues: any,
	passengerValues: PassengerField[],
	accompanyingValues: PassengerField[],
	ignoreFields: PassengerField[] = []
): boolean => {
	return (
		passengerValues
			.filter(field => !ignoreFields.find(ignoredField => ignoredField?.name !== field?.name))
			.some(field => {
				return !!validate(passengerFormValues[field.name], passengerFormValues, field.input, field.validations);
			}) ||
		accompanyingValues
			?.filter(field => !ignoreFields.find(ignoredField => ignoredField.name !== field.name))
			?.some(field => {
				return !!validate(customerFormValues[field.name], customerFormValues, field.input, field.validations);
			}) ||
		!!validateDisabilities(passengerFormValues)
	);
};

export const getPassengerDataLabel = (passengerValues: any, fieldsMap: FieldsMap) => {
	const labels: {
		[type: string]: string;
	} = {};

	if (passengerValues.hasOwnProperty(TravellerFields.Gender)) {
		labels[TravellerFields.Gender] = i18n.t(convertGenderToCorrectValue(passengerValues[TravellerFields.Gender]), {
			ns: 'Passenger'
		});
	}

	labels[TravellerFields.BirthDate] = passengerValues[TravellerFields.BirthDate];

	if (passengerValues.hasOwnProperty(TravellerFields.Nationality)) {
		labels[TravellerFields.Nationality] = fieldsMap[TravellerFields.Nationality].validations?.[0]?.options.find(
			option => option.value === passengerValues[TravellerFields.Nationality]
		)?.label;
	}

	if (passengerValues.hasOwnProperty('docType')) {
		const docTypeValue = passengerValues['docType'];
		const docType =
			fieldsMap['docType'].validations
				.find(
					validation =>
						validation.with === null ||
						validation.with.some(cond => passengerValues[cond.name] === cond.value)
				)
				?.options.find(option => option.value === docTypeValue)?.value ?? docTypeValue;

		labels['docType'] = i18n.t(`Passenger:${docType}`);

		if (labels['docType'] && passengerValues.hasOwnProperty('docNumber')) {
			labels['docNumber'] = passengerValues['docNumber'];
		}
	}

	if (passengerValues.hasOwnProperty('docExpiryDate')) {
		labels['docExpiryDate'] = passengerValues['docExpiryDate'];
	}

	if (passengerValues.hasOwnProperty('loyaltyNumber')) {
		labels['loyaltyNumber'] = passengerValues['loyaltyNumber'];
	}

	return labels;
};

export const getProfileValues = (profile: TravellerProfileFragment) => {
	const profileValuesMap = new Map<TravellerFields | string, string>();

	profile.values.forEach(value => {
		let actualValue = value.value;

		if (value.type === TravellerFieldEnum.Gender && value.value) {
			actualValue = value.value[0];
		}

		profileValuesMap.set(value.name, actualValue);
	});

	return profileValuesMap;
};

export const getMaxPrioritizedOption = memes((options: OptionParameterFragment[]) => {
	if (!options.length) {
		return null;
	}

	if (options.length === 1) {
		return options[0];
	}

	const maxOption = options.reduce((option, currentOption) => {
		return (currentOption.priority ?? 0) > (option.priority ?? 0) ? currentOption : option;
	}, options[0]);

	if (maxOption.priority > 0) {
		return maxOption;
	}

	return null;
});

export const findFitDocument = (
	passenger: PassengerFragment,
	profile: TravellerProfileFragment,
	targetNationality: string = 'RU',
	isInternationalFlight?: boolean,
	actualDocumentType?: string
) => {
	const availableDocument = profile.documents
		.map(doc => {
			let priority = 1;

			if (doc.citizenship === 'RU') {
				switch (doc.type) {
					case DocTypes.InternationalPassportRU:
					case DocTypes.DiplomaticPassport:
						priority = isInternationalFlight ? 100 : 1;
						break;

					case DocTypes.BirthRegDocument:
						priority = isInternationalFlight
							? -1
							: passenger.type === PassengerType.Child ||
							  passenger.type === PassengerType.Infant ||
							  passenger.type === PassengerType.InfantWithSeat
							? 100 // more priority that passport
							: 1;
						break;

					case DocTypes.Passport:
						priority = isInternationalFlight ? -1 : 90;
						break;
				}
			}

			return {
				...doc,
				priority: doc.citizenship === targetNationality ? priority + 1000 : priority
			};
		})
		.filter(doc => doc.priority > 0)
		.sort((a, b) => b.priority - a.priority);

	if (actualDocumentType) {
		return availableDocument.find(doc => doc.type === actualDocumentType) ?? null;
	}

	return availableDocument?.[0] ?? null;
};

export const getActualPassengerValues = (
	passengers: PassengerFragment[],
	profile: TravellerProfileFragment,
	id: number,
	document?: string,
	isInternationalFlight?: boolean,
	isFreezeOrder?: boolean
) => {
	const profileValuesMap = getProfileValues(profile);

	const targetNationality = getMaxPrioritizedOption(
		passengers[0]?.values.find(value => value.name === 'nationality')?.validationRules?.[0]?.options ?? []
	)?.value;

	const documentByType = findFitDocument(passengers[id], profile, targetNationality, isInternationalFlight, document);

	if (documentByType && !isFreezeOrder) {
		documentByType.values.forEach(doc => {
			if (doc.value) {
				let actualValue = doc.value;

				if (doc.type === TravellerFieldEnum.DocExpiryDate) {
					const isRequired = doc.validationRules?.find(rule => rule.with?.[0]?.value === documentByType.type)
						?.required;

					if (isRequired) {
						actualValue = format(parseISO(doc.value), 'dd.MM.yyyy');
					} else {
						actualValue = '';
					}
				}

				profileValuesMap.set(doc.name === 'citizenship' ? 'nationality' : doc.name, actualValue);
			} else {
				profileValuesMap.set(doc.name, '');
			}
		});
	}

	return profileValuesMap;
};

export function isNameFieldType(type: TravellerFieldEnum, types: TravellerFieldEnum[] = []): boolean {
	const isFirstName = type === TravellerFieldEnum.FirstName || types.includes(TravellerFieldEnum.FirstName);
	const isLastName = type === TravellerFieldEnum.LastName || types.includes(TravellerFieldEnum.LastName);
	const isMiddleName = type === TravellerFieldEnum.MiddleName || types.includes(TravellerFieldEnum.MiddleName);

	return isFirstName || isLastName || isMiddleName;
}

export function isDateFieldType(type: TravellerFieldEnum): boolean {
	return type === TravellerFieldEnum.BirthDate || type === TravellerFieldEnum.DocExpiryDate;
}
